import React, { useEffect, useRef, useState } from 'react';
import { MapPin, ArrowRight, TrendingUp, ShieldCheck, Sparkles } from 'lucide-react';

// Simple in-view count-up so hero stats animate the first time they scroll in.
const useCountUp = (target, duration = 1400, decimals = 0) => {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') { setV(target); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setV(target * eased);
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  const value = decimals > 0 ? v.toFixed(decimals) : Math.round(v);
  return [value, ref];
};

const HeroStat = ({ target, suffix, label, delay = 0 }) => {
  const [value, ref] = useCountUp(target, 1600);
  return (
    <div ref={ref} className="text-center am-reveal" style={{ transitionDelay: `${delay}ms` }} data-testid={`hero-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-3xl md:text-4xl font-black text-white mb-1 tabular-nums">
        {value}{suffix}
      </div>
      <div className="text-sm text-slate-300">{label}</div>
    </div>
  );
};

export const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background — static "India growing" image with iconic landmarks + modern skyline */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src="/hero-india-landmarks.jpg"
          alt="India's growth — Taj Mahal, India Gate, Charminar and a modern skyline at golden hour"
          loading="eager"
          className="w-full h-full object-cover object-[65%_45%] md:object-center"
          style={{ filter: 'contrast(1.06) saturate(1.08)', transform: 'scale(1.02)', transformOrigin: 'center', animation: 'am-hero-drift 22s ease-in-out infinite alternate' }}
        />
        {/* Softer overlay on mobile — image was getting drowned. Only enough to keep text legible. */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/55 via-slate-900/25 to-slate-900/10 md:from-slate-900/60 md:via-slate-900/30 md:to-slate-900/10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent"></div>
        {/* Floating decorative orbs — subtle, GPU-cheap */}
        <div className="absolute -top-32 -left-24 w-96 h-96 bg-emerald-500/25 rounded-full blur-3xl mix-blend-screen animate-am-orb-1 pointer-events-none" />
        <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] bg-amber-400/25 rounded-full blur-3xl mix-blend-screen animate-am-orb-2 pointer-events-none" />
        <div className="absolute -bottom-24 left-1/3 w-[22rem] h-[22rem] bg-sky-400/20 rounded-full blur-3xl mix-blend-screen animate-am-orb-3 pointer-events-none" />
        <style>{`
          @keyframes am-hero-drift {
            0%   { transform: scale(1.02) translate(0, 0); }
            100% { transform: scale(1.05) translate(-1.5%, -0.8%); }
          }
          @keyframes am-orb-1 { 0%,100% { transform: translate(0,0) scale(1) } 50% { transform: translate(30px,20px) scale(1.08) } }
          @keyframes am-orb-2 { 0%,100% { transform: translate(0,0) scale(1) } 50% { transform: translate(-40px,25px) scale(1.05) } }
          @keyframes am-orb-3 { 0%,100% { transform: translate(0,0) scale(1) } 50% { transform: translate(20px,-25px) scale(1.1) } }
          .animate-am-orb-1 { animation: am-orb-1 12s ease-in-out infinite; }
          .animate-am-orb-2 { animation: am-orb-2 14s ease-in-out infinite; }
          .animate-am-orb-3 { animation: am-orb-3 16s ease-in-out infinite; }
        `}</style>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6 animate-fade-in am-reveal">
            <MapPin size={18} className="text-amber-400" />
            <span className="text-white text-sm font-medium">Your Trusted Real Estate Partner</span>
            <span className="hidden sm:inline-flex items-center gap-1 ml-1 text-[10px] font-black uppercase tracking-widest bg-emerald-500/25 border border-emerald-300/40 text-emerald-100 rounded-full px-2 py-0.5">
              <Sparkles size={10} /> Verified
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight am-reveal">
            India is growing every day.
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-amber-400 to-emerald-400 mt-2 am-grad-shift">
              Don&rsquo;t wait for the right time.
            </span>
            <span className="block text-white/90 text-3xl md:text-4xl lg:text-5xl mt-3">Find the right property.</span>
          </h1>

          {/* Description */}
          <p className="text-xl text-slate-200 mb-6 leading-relaxed am-reveal" style={{ transitionDelay: '80ms' }}>
            Premium residential plots, agricultural land, and commercial parcels along India&rsquo;s fastest-growing corridors — metro extensions, greenfield airports, and expressways. Curated, verified, and priced honestly by Acre Mitra.
          </p>

          {/* Trust chips */}
          <div className="flex flex-wrap gap-2 mb-8 am-reveal" style={{ transitionDelay: '140ms' }}>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-100 bg-emerald-900/50 border border-emerald-300/40 rounded-full px-3 py-1.5 backdrop-blur">
              <ShieldCheck size={12} /> Title-verified
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-amber-100 bg-amber-900/40 border border-amber-300/40 rounded-full px-3 py-1.5 backdrop-blur">
              <TrendingUp size={12} /> 10-yr ROI report free
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-sky-100 bg-sky-900/40 border border-sky-300/40 rounded-full px-3 py-1.5 backdrop-blur">
              <MapPin size={12} /> Satellite HT-wire scan
            </span>
          </div>

          {/* CTAs — using new pill buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 am-reveal" style={{ transitionDelay: '200ms' }}>
            <button
              onClick={() => scrollToSection('projects')}
              className="btn-am-primary text-base px-9 py-4 group"
              data-testid="hero-explore-btn"
            >
              Explore Properties
              <ArrowRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center justify-center gap-2 rounded-full text-white border-2 border-white/40 px-9 py-4 text-base font-semibold hover:bg-white hover:text-emerald-700 transition-all duration-300 backdrop-blur-md bg-white/10"
              data-testid="hero-schedule-btn"
            >
              Schedule Site Visit
            </button>
          </div>

          {/* Stats — animated counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <HeroStat target={500} suffix="+" label="Properties Sold" delay={0} />
            <HeroStat target={1000} suffix="+" label="Happy Clients" delay={80} />
            <HeroStat target={50} suffix="+" label="Active Projects" delay={160} />
            <HeroStat target={15} suffix="+" label="Years Experience" delay={240} />
          </div>
        </div>
      </div>

      {/* Decorative bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};
